import React, { useCallback, useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import classNames from 'classnames';
// => Tiptap packages
import { useEditor, EditorContent, Editor, BubbleMenu } from '@tiptap/react';
import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import Link from '@tiptap/extension-link';
import Bold from '@tiptap/extension-bold';
import Underline from '@tiptap/extension-underline';
import Italic from '@tiptap/extension-italic';
import Strike from '@tiptap/extension-strike';
import Code from '@tiptap/extension-code';
import History from '@tiptap/extension-history';
// Custom
import * as Icons from '../icons';
import { LinkModal } from './LinkModal';
import { LanguageTool, Match } from './languagetool';
import './styles.css';

interface TipTapEditorProps {
    initialValue: string;
    onChange: (html: string) => void;
}

const TipTapEditor = ({ initialValue, onChange }: TipTapEditorProps) => {
    const editorContainerRef = useRef<HTMLDivElement>(null);
    const popupRef = useRef<HTMLDivElement>(null);
    const enableSpellchecker = import.meta.env.VITE_ENABLE_SPELLCHECKER === 'true';

    // Configure LanguageTool extension based on env vars
    const languageToolExtension = useMemo(() => {
        if (!enableSpellchecker) return null;
        
        // Use the public LanguageTool API by default
        // You can also self-host LanguageTool: https://dev.languagetool.org/http-server
        const apiUrl = import.meta.env.VITE_LANGUAGETOOL_API_URL || 'https://api.languagetool.org/v2/check';
        
        return LanguageTool.configure({
            language: 'en-US',
            apiUrl,
            automaticMode: true,
        });
    }, [enableSpellchecker]);

    const extensions = useMemo(() => {
        const baseExtensions: any[] = [
            Document,
            History,
            Paragraph,
            Text,
            Link.configure({
                openOnClick: false,
            }),
            Bold,
            Underline,
            Italic,
            Strike,
            Code,
        ];

        if (languageToolExtension) {
            baseExtensions.push(languageToolExtension);
        }

        return baseExtensions;
    }, [languageToolExtension]);

    const editor = useEditor({
        extensions,
        content: initialValue || '',
        editorProps: {
            attributes: {
                // Disable browser spellcheck when LanguageTool is enabled
                spellcheck: enableSpellchecker ? 'false' : 'true',
            },
        },
        onUpdate: ({ editor }) => {
            const html = editor.getHTML();
            onChange(html);
        },
    }) as Editor;

    // Get current match from LanguageTool storage
    const [currentMatch, setCurrentMatch] = useState<Match | null>(null);
    const [popupPosition, setPopupPosition] = useState<{ top: number; left: number; arrowLeft?: number } | null>(null);
    const [originalWord, setOriginalWord] = useState<string>('');
    const isPopupOpenRef = useRef(false);
    // Track dismissed match to prevent re-opening when editor selection changes
    const dismissedMatchRangeRef = useRef<{ from: number; to: number } | null>(null);
    const dismissTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Helper to clear popup state
    const clearPopup = useCallback((markAsDismissed = false, matchRange?: { from: number; to: number }) => {
        setCurrentMatch(null);
        setPopupPosition(null);
        setOriginalWord('');
        isPopupOpenRef.current = false;
        
        // Clear any existing dismiss timeout
        if (dismissTimeoutRef.current) {
            clearTimeout(dismissTimeoutRef.current);
            dismissTimeoutRef.current = null;
        }
        
        if (markAsDismissed && matchRange) {
            dismissedMatchRangeRef.current = matchRange;
            // Clear the dismissed flag after a short delay to allow future hovers
            // This prevents immediate re-open from the click event, but allows
            // the user to hover again to see the suggestion
            dismissTimeoutRef.current = setTimeout(() => {
                dismissedMatchRangeRef.current = null;
            }, 300);
        }
    }, []);

    // Calculate popup position with viewport boundary detection
    const calculatePopupPosition = useCallback((rect: DOMRect) => {
        const POPUP_WIDTH = 300; // max-width from CSS
        const POPUP_HEIGHT_ESTIMATE = 150; // rough estimate
        const MARGIN = 10;
        
        let left = rect.left + (rect.width / 2);
        let top = rect.top - MARGIN;
        let arrowLeft: number | undefined = undefined;
        
        // Check left boundary - prevent popup from going off left edge
        const leftEdge = left - (POPUP_WIDTH / 2);
        if (leftEdge < MARGIN) {
            const adjustment = MARGIN - leftEdge;
            left += adjustment;
            // Arrow should point to original position (center of word)
            arrowLeft = (POPUP_WIDTH / 2) - adjustment;
        }
        
        // Check right boundary - prevent popup from going off right edge
        const rightEdge = left + (POPUP_WIDTH / 2);
        if (rightEdge > window.innerWidth - MARGIN) {
            const adjustment = rightEdge - (window.innerWidth - MARGIN);
            left -= adjustment;
            // Arrow should point to original position (center of word)
            arrowLeft = (POPUP_WIDTH / 2) + adjustment;
        }
        
        // Check top boundary - if popup would go above viewport, show below word instead
        if (top - POPUP_HEIGHT_ESTIMATE < MARGIN) {
            top = rect.bottom + MARGIN + POPUP_HEIGHT_ESTIMATE;
        }
        
        return { top, left, arrowLeft };
    }, []);

    // Update current match when editor storage changes
    useEffect(() => {
        if (!editor || !enableSpellchecker) return;

        const updateMatch = () => {
            const match = editor.storage.languagetool?.match;
            const matchRange = editor.storage.languagetool?.matchRange;
            
            if (match && matchRange && matchRange.from >= 0) {
                // Check if this match was dismissed by the user
                const dismissed = dismissedMatchRangeRef.current;
                if (dismissed && dismissed.from === matchRange.from && dismissed.to === matchRange.to) {
                    // User dismissed this exact match, don't re-open
                    return;
                }
                
                // Clear dismissed flag since we're looking at a different match
                dismissedMatchRangeRef.current = null;
                
                // Check if the decoration element still exists
                const decorationElement = document.querySelector(`span.lt[data-match*='"from":${matchRange.from}']`);
                if (decorationElement) {
                    setCurrentMatch(match);
                    isPopupOpenRef.current = true;
                    
                    // Get the original word from the document
                    try {
                        const word = editor.state.doc.textBetween(matchRange.from, matchRange.to);
                        setOriginalWord(word);
                    } catch {
                        // Range is invalid (word was deleted)
                        clearPopup();
                        return;
                    }
                    
                    // Calculate position with boundary detection
                    const rect = decorationElement.getBoundingClientRect();
                    setPopupPosition(calculatePopupPosition(rect));
                } else {
                    // Decoration element no longer exists (word was modified/deleted)
                    clearPopup();
                }
            } else {
                clearPopup();
                // Clear dismissed match when no match is active
                dismissedMatchRangeRef.current = null;
            }
        };

        // Check for match updates on selection change
        editor.on('selectionUpdate', updateMatch);
        editor.on('transaction', updateMatch);

        return () => {
            editor.off('selectionUpdate', updateMatch);
            editor.off('transaction', updateMatch);
        };
    }, [editor, enableSpellchecker, clearPopup, calculatePopupPosition]);

    // Handle click outside to dismiss popup - use capture phase for reliability
    useEffect(() => {
        if (!editor) return;
        
        const handleClickOutside = (event: MouseEvent) => {
            // Only process if popup is actually open
            if (!isPopupOpenRef.current) return;
            
            const target = event.target as Node;
            
            // Don't dismiss if clicking inside the popup
            if (popupRef.current && popupRef.current.contains(target)) {
                return;
            }
            
            // Don't dismiss if clicking on a spell-check decoration (opening another suggestion)
            if (target instanceof HTMLElement && target.closest('span.lt')) {
                return;
            }
            
            // Dismiss the popup and mark the match as dismissed to prevent re-opening
            const matchRange = editor.storage.languagetool?.matchRange;
            clearPopup(true, matchRange);
        };

        // Use capture phase to ensure we catch clicks before they bubble
        document.addEventListener('mousedown', handleClickOutside, true);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside, true);
        };
    }, [editor, clearPopup]);

    // Cleanup timeout on unmount
    useEffect(() => {
        return () => {
            if (dismissTimeoutRef.current) {
                clearTimeout(dismissTimeoutRef.current);
            }
        };
    }, []);

    // Helper function to match capitalization pattern
    const matchCapitalization = (original: string, replacement: string): string => {
        if (!original || !replacement) return replacement;
        
        // Check if original is all uppercase
        if (original === original.toUpperCase()) {
            return replacement.toUpperCase();
        }
        
        // Check if original is all lowercase
        if (original === original.toLowerCase()) {
            return replacement.toLowerCase();
        }
        
        // Check if original is capitalized (first letter uppercase, rest lowercase)
        if (original[0] === original[0].toUpperCase() && 
            original.slice(1) === original.slice(1).toLowerCase()) {
            return replacement.charAt(0).toUpperCase() + replacement.slice(1).toLowerCase();
        }
        
        // For mixed case or other patterns, try to match character by character
        let result = '';
        for (let i = 0; i < replacement.length; i++) {
            if (i < original.length) {
                if (original[i] === original[i].toUpperCase()) {
                    result += replacement[i].toUpperCase();
                } else {
                    result += replacement[i].toLowerCase();
                }
            } else {
                // For extra characters in replacement, use lowercase
                result += replacement[i].toLowerCase();
            }
        }
        return result;
    };

    const acceptSuggestion = useCallback((suggestion: string) => {
        if (!editor || !currentMatch) return;
        
        const matchRange = editor.storage.languagetool?.matchRange;
        if (matchRange && matchRange.from >= 0 && matchRange.to >= 0) {
            // Apply capitalization matching
            const adjustedSuggestion = matchCapitalization(originalWord, suggestion);
            
            editor
                .chain()
                .focus()
                .deleteRange({ from: matchRange.from, to: matchRange.to })
                .insertContentAt(matchRange.from, adjustedSuggestion)
                .run();
        }
        clearPopup();
    }, [editor, currentMatch, originalWord, clearPopup]);

    const ignoreMatch = useCallback(() => {
        if (!editor) return;
        editor.commands.ignoreLanguageToolSuggestion();
        clearPopup();
    }, [editor, clearPopup]);
    const [modalIsOpen, setIsOpen] = useState(false);
    const [url, setUrl] = useState<string>('');

    const openModal = useCallback(() => {
        console.log(editor.chain().focus());
        setUrl(editor.getAttributes('link').href);
        setIsOpen(true);
    }, [editor]);

    const closeModal = useCallback(() => {
        setIsOpen(false);
        setUrl('');
    }, []);

    const saveLink = useCallback(() => {
        if (url) {
            editor
                .chain()
                .focus()
                .extendMarkRange('link')
                .setLink({ href: url, target: '_blank' })
                .run();
        } else {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
        }
        closeModal();
    }, [editor, url, closeModal]);

    const removeLink = useCallback(() => {
        editor.chain().focus().extendMarkRange('link').unsetLink().run();
        closeModal();
    }, [editor, closeModal]);

    const toggleBold = useCallback(() => {
        editor.chain().focus().toggleBold().run();
    }, [editor]);

    const toggleUnderline = useCallback(() => {
        editor.chain().focus().toggleUnderline().run();
    }, [editor]);

    const toggleItalic = useCallback(() => {
        editor.chain().focus().toggleItalic().run();
    }, [editor]);

    const toggleStrike = useCallback(() => {
        editor.chain().focus().toggleStrike().run();
    }, [editor]);

    const toggleCode = useCallback(() => {
        editor.chain().focus().toggleCode().run();
    }, [editor]);

    if (!editor) {
        return null;
    }

    useEffect(() => {
        if (!editor) return;
        let { from, to } = editor.state.selection;
        editor.commands.setContent(initialValue, false, {
            preserveWhitespace: 'full',
        });
        editor.commands.setTextSelection({ from, to });
    }, [editor, initialValue]);

    return (
        <div className='editor form-control' ref={editorContainerRef}>
            <div className='menu'>
                <button
                    type='button'
                    className='menu-button'
                    onClick={() => editor.chain().focus().undo().run()}
                    disabled={!editor.can().undo()}
                >
                    <Icons.RotateLeft />
                </button>
                <button
                    type='button'
                    className='menu-button'
                    onClick={() => editor.chain().focus().redo().run()}
                    disabled={!editor.can().redo()}
                >
                    <Icons.RotateRight />
                </button>
                <button
                    type='button'
                    className={classNames('menu-button', {
                        'is-active': editor.isActive('link'),
                    })}
                    onClick={openModal}
                >
                    <Icons.Link />
                </button>
                <button
                    type='button'
                    className={classNames('menu-button', {
                        'is-active': editor.isActive('bold'),
                    })}
                    onClick={toggleBold}
                >
                    <Icons.Bold />
                </button>
                <button
                    type='button'
                    className={classNames('menu-button', {
                        'is-active': editor.isActive('underline'),
                    })}
                    onClick={toggleUnderline}
                >
                    <Icons.Underline />
                </button>
                <button
                    type='button'
                    className={classNames('menu-button', {
                        'is-active': editor.isActive('intalic'),
                    })}
                    onClick={toggleItalic}
                >
                    <Icons.Italic />
                </button>
                <button
                    type='button'
                    className={classNames('menu-button', {
                        'is-active': editor.isActive('strike'),
                    })}
                    onClick={toggleStrike}
                >
                    <Icons.Strikethrough />
                </button>
                <button
                    type='button'
                    className={classNames('menu-button', {
                        'is-active': editor.isActive('code'),
                    })}
                    onClick={toggleCode}
                >
                    <Icons.Code />
                </button>
            </div>

            <BubbleMenu
                className='bubble-menu-light'
                tippyOptions={{ duration: 150 }}
                editor={editor}
                shouldShow={({ editor, view, state, oldState, from, to }) => {
                    // only show the bubble menu for links.
                    return from === to && editor.isActive('link');
                }}
            >
                <button type='button' className='button' onClick={openModal}>
                    Edit
                </button>
                <button
                    type='button'
                    className='button-remove'
                    onClick={removeLink}
                >
                    Remove
                </button>
            </BubbleMenu>

            {/* LanguageTool suggestion bubble menu - rendered via portal to escape overflow clipping */}
            {enableSpellchecker && currentMatch && popupPosition && createPortal(
                <div 
                    ref={popupRef}
                    className='lt-bubble-menu' 
                    style={{
                        position: 'fixed',
                        top: `${popupPosition.top}px`,
                        left: `${popupPosition.left}px`,
                        transform: 'translate(-50%, -100%)',
                        zIndex: 1000,
                        // Override arrow position if popup was shifted for boundary
                        ['--arrow-left' as any]: popupPosition.arrowLeft !== undefined 
                            ? `${popupPosition.arrowLeft}px` 
                            : '50%',
                    }}
                >
                    <p className='lt-bubble-menu__message'>
                        {currentMatch.message}
                    </p>
                    {currentMatch.replacements.length > 0 && (
                        <div className='lt-bubble-menu__suggestions'>
                            {currentMatch.replacements.slice(0, 5).map((replacement, i) => (
                                <button
                                    key={i}
                                    type='button'
                                    className='lt-bubble-menu__suggestion'
                                    onClick={() => acceptSuggestion(replacement.value)}
                                >
                                    {matchCapitalization(originalWord, replacement.value)}
                                </button>
                            ))}
                        </div>
                    )}
                    <div className='lt-bubble-menu__actions'>
                        <button
                            type='button'
                            className='lt-bubble-menu__ignore'
                            onClick={ignoreMatch}
                        >
                            Ignore
                        </button>
                    </div>
                </div>,
                document.body
            )}

            <EditorContent editor={editor} />

            <LinkModal
                url={url}
                isOpen={modalIsOpen}
                closeModal={closeModal}
                onChangeUrl={(e) => setUrl(e.target.value)}
                onSaveLink={saveLink}
                onRemoveLink={removeLink}
            />
        </div>
    );
};

export default TipTapEditor;
